import "./UserGroupRoles.css";
import { useEffect, useState } from "react";
import { toastSuccess } from "../../../components/base/Toast";
import Form from "../../../components/form/Form";
import Roles from "../../../core/Roles.json";
import { useHub } from "../../../core/Global";
import Icon from "../../../components/base/Icon";
import Div from "../../../components/base/Div";
import Indicator from "../../../components/base/Indicator";
import { API_ROUTES } from "../../../core/Constants";
import UserGroup from "../models/UserGroup";
import { groupRoleCategories } from "../utils/roleCategories";


type Props = {
    model: UserGroup
    groupSlug?: string
}


export default function UserGroupRolesForm(props: Props) {

    const hub = useHub();
    const [working, workingSet] = useState<boolean>(false);
    const [loading, loadingSet] = useState<boolean>(true);

    const [rolesInherited, rolesInheritedSet] = useState<string[]>();
    const [rolesGranted, rolesGrantedSet] = useState<string[]>();
    const [rolesDenied, rolesDeniedSet] = useState<string[]>();
    const [rolesAllowed, rolesAllowedSet] = useState<string[]>([]);
    const [AllRolesAllowed, AllRolesAllowedSet] = useState<boolean>();

    const [sourcesData, sourcesDataSet] = useState<{ id?: string, name?: string }[]>([]);
    const [jobTypesData, jobTypesDataSet] = useState<{ id?: string, name?: string }[]>([]);
    const [metrosData, metrosDataSet] = useState<{ id?: string, name?: string }[]>([]);
    const [techniciansData, techniciansDataSet] = useState<{ id?: string, name?: string }[]>([]);
    const [search, searchSet] = useState<string>("");

    // the currently selected category (defaults to the first one)
    const category = groupRoleCategories.find(c => c.slug == props.groupSlug) ?? groupRoleCategories[0];
    const isSources = category.slug == "sources";
    const isJobTypes = category.slug == "job-types";
    const isMetros = category.slug == "metros";
    const isTechnicians = category.slug == "technicians";
    const isRestriction = isSources || isJobTypes || isMetros || isTechnicians;
    const group: any = isRestriction ? undefined : Roles.find(g => g.title == category.title);

    const fetch = () => {
        loadingSet(true);
        hub.Get(API_ROUTES.UsersGroups + "roles-editor/" + props.model.id, res => {
            if (res.success) {
                const rd = res.data.rolesData;
                rolesInheritedSet(rd.rolesInherited);
                rolesGrantedSet(rd.rolesGranted);
                rolesDeniedSet(rd.rolesDenied);
                rolesAllowedSet(rd.rolesAllowed ?? []);
                AllRolesAllowedSet(rd.allRolesAllowed);
                sourcesDataSet(res.data.sourcesData ?? []);
                jobTypesDataSet(res.data.jobTypesData ?? []);
                metrosDataSet(res.data.metrosData ?? []);
                techniciansDataSet(res.data.techniciansData ?? []);
                loadingSet(false);
            }
        });
    }

    useEffect(() => {
        fetch();
    }, [props.model.id])

    // a stale keyword would hide everything after switching category
    useEffect(() => searchSet(""), [props.groupSlug])

    // client-side search over the visible rows (source/metro/... names, or role titles)
    const matchesSearch = (...texts: (string | undefined)[]) => {
        if (window.isNullOrEmpty(search)) return true;
        const q = search.toLowerCase();
        return texts.some(t => (t ?? "").toLowerCase().indexOf(q) > -1);
    }

    const AddRole = (arr?: string[], value?: string): string[] => {
        if (!value) return [];
        if (!arr) return [value ?? ""];
        var exists = false;
        for (var i = 0; i < arr.length; i++) {
            if (arr[i] == value) {
                exists = true;
                break;
            }
        }
        if (!exists) arr.push(value);
        else arr = RemoveRole(arr, value);
        return arr;
    }

    const RemoveRole = (arr?: string[], value?: string): string[] => {
        if (!arr) return [];
        return arr.filter(function (ele) {
            return ele != value;
        });
    }

    const setGrant = (key: string) => {
        var RolesDenied = RemoveRole(rolesDenied, key);
        var RolesGranted = AddRole(rolesGranted, key);
        rolesGrantedSet(r => [...RolesGranted]);
        rolesDeniedSet(r => [...RolesDenied]);
    }

    const setDeny = (key: string) => {
        var RolesGranted = RemoveRole(rolesGranted, key);
        var RolesDenied = AddRole(rolesDenied, key);
        rolesGrantedSet(r => [...RolesGranted]);
        rolesDeniedSet(r => [...RolesDenied]);
    }

    // access-restriction block (Sources / Job Types / Metros / Technicians) - group
    // version has no "Default" column: an "All X" grant that reveals per-item rows when off.
    const renderRestrictionBlock = (allKey: string, prefix: string, allLabel: string, items: { id?: string, name?: string }[]) => {
        const allGranted = !!rolesGranted && rolesGranted.indexOf(allKey) > -1;
        return (
            <tbody>
                {
                    matchesSearch(allLabel) &&
                    <tr>
                        <td>
                            <div className="fw-bold fs-6 ps-2">{allLabel}</div>
                        </td>
                        <td>
                            <label className={"form-check form-switch form-check-custom  form-check-solid"}>
                                <input className="form-check-input" type="checkbox" disabled
                                    checked={rolesInherited?.includes(allKey)} />
                            </label>
                        </td>
                        <td>
                            <label className={"form-check form-switch form-check-custom  form-check-solid"}>
                                <input className="form-check-input" name="grantedRoles" type="checkbox"
                                    onChange={() => setGrant(allKey)}
                                    value={allKey} checked={rolesGranted?.includes(allKey)} />
                            </label>
                        </td>
                        <td></td>
                    </tr>
                }
                {
                    !allGranted && items.filter(it => matchesSearch(it.name)).map((it, k) => {
                        const key = prefix + "-" + it.id;
                        return (
                            <tr key={k}>
                                <td>
                                    <div className="fw-bold fs-6 ps-2">{it.name}</div>
                                </td>
                                <td>
                                    <label className={"form-check form-switch form-check-custom  form-check-solid"}>
                                        <input className="form-check-input" type="checkbox" disabled
                                            checked={rolesInherited?.includes(key)} />
                                    </label>
                                </td>
                                <td>
                                    <label className={"form-check form-switch form-check-custom  form-check-solid"}>
                                        <input className="form-check-input" name="grantedRoles" type="checkbox"
                                            onChange={() => setGrant(key)}
                                            value={key} checked={rolesGranted?.includes(key)} />
                                    </label>
                                </td>
                                <td>
                                    <label className={"form-check form-switch form-check-custom  form-check-solid form-check-danger"}>
                                        <input className="form-check-input" name="deniedRoles" type="checkbox"
                                            onChange={() => setDeny(key)}
                                            value={key} checked={rolesDenied?.includes(key)} />
                                    </label>
                                </td>
                            </tr>
                        );
                    })
                }
            </tbody>
        );
    }


    return (
        <Form id="user-roles-form" className="form"
            onSubmit={() => {

                workingSet(true);
                hub.Put("/accounts/usergroups/roles/" + props.model.id, {
                    rolesGranted: rolesGranted,
                    rolesDenied: rolesDenied
                }, res => {
                    if (res.success) {
                        toastSuccess("Group roles has been updated successfully!");
                    }
                    workingSet(false);
                });

            }}>
            <div className="card mb-5 mb-xl-10">
                <div className="card-header border-0 grp-roles-header">
                    <div className="card-title m-0">
                        <h3 className="fw-bolder m-0 me-5">Roles &amp; Permissions</h3>
                        <div className="grp-roles-search">
                            <span className="grp-roles-search-icon">
                                <Icon icon="magnifying-glass" size={13} />
                            </span>
                            <input type="text" className="form-control form-control-sm"
                                placeholder={`Search in ${category.title}...`}
                                value={search}
                                onChange={e => searchSet(e.target.value)} />
                        </div>
                    </div>
                    <div className="card-action">
                        <button type="submit" className="btn btn-sm btn-primary" disabled={working}>
                            {
                                working ?
                                    <>
                                        <Icon icon="spinner-third" spin />
                                        <span className="ms-2">Updating...</span>
                                    </>
                                    :
                                    <>Update Roles</>
                            }
                        </button>
                    </div>
                </div>

                <div className="card-body border-top p-9">

                    <Div visible={loading} className="p-20 text-center">
                        <Indicator text="Please wait..." />
                    </Div>

                    <Div visible={!loading}>

                        <table className="table table-striped table-roles">
                            <thead>
                                <tr>
                                    <th className="fs-4 fw-bolder">
                                        {category.title}
                                    </th>
                                    <th className="action-col">
                                        Inherited
                                    </th>
                                    <th className="action-col">
                                        Grant
                                    </th>
                                    <th className="action-col">
                                        Deny
                                    </th>
                                </tr>
                            </thead>

                            {
                                isSources && renderRestrictionBlock("SourcesAll", "Sources", "All Sources", sourcesData)
                            }

                            {
                                isJobTypes && renderRestrictionBlock("JobTypesAll", "JobTypes", "All Job Types", jobTypesData)
                            }

                            {
                                isMetros && renderRestrictionBlock("MetrosAll", "Metros", "All Metros", metrosData)
                            }

                            {
                                isTechnicians && renderRestrictionBlock("TechniciansAll", "Technicians", "All Technicians", techniciansData)
                            }

                            {
                                group &&
                                <tbody>
                                    {
                                        group.roles
                                            .filter((f: any) => AllRolesAllowed || rolesAllowed.indexOf(f.roleName) > -1)
                                            .filter((f: any) => matchesSearch(f.title, f.description))
                                            .map((role: any, roleKey: number) =>
                                            <tr key={roleKey}>
                                                <td>
                                                    <div className="fw-bold fs-6">
                                                        {role.title}
                                                    </div>
                                                    <div className={`fs-8 ${(role.danger ? "text-danger" : "text-muted")}`}>
                                                        {role.description}
                                                    </div>
                                                </td>
                                                <td>
                                                    <label className={"form-check form-switch form-check-custom  form-check-solid"}>
                                                        <input className="form-check-input" type="checkbox" disabled
                                                            checked={rolesInherited?.includes(role.roleName)} />
                                                    </label>
                                                </td>
                                                <td>
                                                    <label className={"form-check form-switch form-check-custom  form-check-solid"}>
                                                        <input className="form-check-input" name="grantedRoles" type="checkbox"
                                                            onChange={() => setGrant(role.roleName)}
                                                            value={role.roleName} checked={rolesGranted?.includes(role.roleName)} />
                                                    </label>
                                                </td>
                                                <td>
                                                    <label className={"form-check form-switch form-check-custom  form-check-solid form-check-danger"}>
                                                        <input className="form-check-input" name="deniedRoles" type="checkbox"
                                                            onChange={() => setDeny(role.roleName)}
                                                            value={role.roleName} checked={rolesDenied?.includes(role.roleName)} />
                                                    </label>
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            }

                        </table>

                    </Div>

                </div>

            </div>
        </Form>
    )
}
